import axios from 'axios';

/**
 * Wording of the ledger conflict raised by BillingLedgerService.insertInvoiceInTx
 * when the create operation key is already linked to an annulled invoice. The
 * response body carries no machine-readable code for this case, so the stable
 * phrase is the only discriminator available; the message text itself is never
 * replaced in the UI (the operator always sees the backend's exact string).
 */
const ANNULLED_INVOICE_PATTERN = /linked to annulled invoice/i;
const ANNULLED_INVOICE_ID_PATTERN = /linked to annulled invoice\s+#?(\d+)/i;

export interface CobroCreateFailure {
  /** Backend message exactly as sent, or null when the body carries none. */
  message: string | null;
  /** Annulled invoice id when the backend named it, else null. */
  annulledInvoiceId: number | null;
  /** True only for the annulled-invoice conflict (the recreate confirmation gate). */
  isAnnulledInvoiceConflict: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** NestJS sends `message` as a string, or as a string[] for validation errors. */
function readBackendMessage(data: Record<string, unknown>): string | null {
  const raw = data.message;
  if (typeof raw === 'string' && raw.trim().length > 0) {
    return raw;
  }
  if (Array.isArray(raw)) {
    const first = raw.find(
      (entry): entry is string => typeof entry === 'string' && entry.trim().length > 0,
    );
    return first ?? null;
  }
  return null;
}

export function readCobroCreateFailure(error: unknown): CobroCreateFailure {
  if (!axios.isAxiosError(error) || !isRecord(error.response?.data)) {
    return { message: null, annulledInvoiceId: null, isAnnulledInvoiceConflict: false };
  }

  const message = readBackendMessage(error.response.data);
  if (message === null || !ANNULLED_INVOICE_PATTERN.test(message)) {
    return { message, annulledInvoiceId: null, isAnnulledInvoiceConflict: false };
  }

  const idMatch = ANNULLED_INVOICE_ID_PATTERN.exec(message);
  const id = idMatch?.[1];
  return {
    message,
    annulledInvoiceId: id === undefined ? null : Number(id),
    isAnnulledInvoiceConflict: true,
  };
}

/**
 * Explicit, deterministic per-annulment operation key. The same confirmation
 * retried (even after reopening the dialog) mints the same key, so the ledger
 * converges idempotently; a later annulment names a different invoice id and
 * therefore a different key. Never collides with the backend's derived
 * `manual:` key for the same period.
 */
export function buildRecreationOperationKey(
  values: Record<string, unknown>,
  annulledInvoiceId: number | null,
): string {
  const parts = [
    'recreacion',
    typeof values.tipoFactura === 'string' ? values.tipoFactura : '',
    String(values.idCliente ?? ''),
    String(values.fechaInicio ?? values.fechaCobro ?? ''),
    String(values.valor ?? ''),
    typeof values.concepto === 'string' ? values.concepto.trim() : '',
    annulledInvoiceId === null ? '' : String(annulledInvoiceId),
  ];
  return parts.filter((part) => part.length > 0).join(':');
}
